import { action, autorun, computed, IReactionDisposer, makeObservable, observable } from 'mobx';
import { CandidateEditProfileEmptyResponse } from 'candidate/types';
import { HttpClient } from 'shared/libs/http';
import { AsyncState } from 'shared/libs/state';

import { DesiredPositionStore } from './desired-position/desired-position.store';
import { WorkExperienceStore } from './work-experience/work-experience.store';
import { EducationInfoStore } from './education-info/education-info.store';
import { AdditionalInfoStore } from './additonal-info/additonal-info.store';

export const CreateResumeStep = {
  DESIRED_POSITION: 'desired_position',
  WORK_EXPERIENCE: 'work_experience',
  EDUCATION_INFO: 'education_info',
  ADDITIONAL_INFO: 'additional_info',
} as const;

export type CreateResumeStep = (typeof CreateResumeStep)[keyof typeof CreateResumeStep];

export const isCreateResumeStep = (value: string | null): value is CreateResumeStep =>
  Object.values(CreateResumeStep).includes(value as CreateResumeStep);

export class CreateResumeStore {
  activeStep: CreateResumeStep = CreateResumeStep.DESIRED_POSITION;
  readonly loadInitialValuesState = new AsyncState<CandidateEditProfileEmptyResponse>();
  private readonly valuesLoggerDisposer: IReactionDisposer;

  readonly stepOrder: CreateResumeStep[] = [
    CreateResumeStep.DESIRED_POSITION,
    CreateResumeStep.WORK_EXPERIENCE,
    CreateResumeStep.EDUCATION_INFO,
    CreateResumeStep.ADDITIONAL_INFO,
  ];

  constructor(
    public readonly desiredPosition: DesiredPositionStore,
    public readonly workExperience: WorkExperienceStore,
    public readonly educationInfo: EducationInfoStore,
    public readonly additionalInfo: AdditionalInfoStore,
    initialStep: CreateResumeStep = CreateResumeStep.DESIRED_POSITION
  ) {
    this.activeStep = initialStep;

    makeObservable(this, {
      activeStep: observable,
      isBootstrapping: computed,
      currentStepIndex: computed,
      canGoBack: computed,
      canGoNext: computed,
      valid: computed,
      isCurrentStepValid: computed,
      valuesSnapshot: computed,
      loadInitialValues: action.bound,
      goBack: action.bound,
      goNext: action.bound,
      goToStep: action.bound,
      setActiveStep: action.bound,
    });

    this.valuesLoggerDisposer = autorun(() => {
      console.log('CreateResumeStore values changed', this.valuesSnapshot);
    });
  }

  get currentStepIndex(): number {
    return this.stepOrder.indexOf(this.activeStep);
  }

  get isBootstrapping(): boolean {
    return this.loadInitialValuesState.status === 'pending';
  }

  get canGoBack(): boolean {
    return this.currentStepIndex > 0;
  }

  get canGoNext(): boolean {
    return (
      this.currentStepIndex < this.stepOrder.length - 1 && this.isCurrentStepValid
    );
  }

  get valid(): boolean {
    return (
      this.desiredPosition.valid &&
      this.workExperience.valid &&
      this.educationInfo.valid &&
      this.additionalInfo.valid
    );
  }

  get isCurrentStepValid(): boolean {
    switch (this.activeStep) {
      case CreateResumeStep.DESIRED_POSITION:
        return this.desiredPosition.valid;
      case CreateResumeStep.WORK_EXPERIENCE:
        return this.workExperience.valid;
      case CreateResumeStep.EDUCATION_INFO:
        return this.educationInfo.valid;
      case CreateResumeStep.ADDITIONAL_INFO:
        return this.additionalInfo.valid;
      default:
        return false;
    }
  }

  get valuesSnapshot() {
    return {
      activeStep: this.activeStep,
      isBootstrapping: this.isBootstrapping,
      desiredPosition: {
        position: this.desiredPosition.position,
        desiredCategories: this.desiredPosition.desiredCategories.map((group) => ({
          categoryId: group.categoryId,
          specializations: group.specializations.values.map((item) => ({
            id: item.id,
            name: item.name,
          })),
        })),
        workTypes: this.desiredPosition.workTypes.values.map((item) => ({
          value: item.value,
          label: item.label,
        })),
        salaryMin: this.desiredPosition.salaryMin,
        salaryMax: this.desiredPosition.salaryMax,
        desiredCountry: this.desiredPosition.desiredCountry,
        desiredCity: this.desiredPosition.desiredCity,
        salaryCurrency: this.desiredPosition.salaryCurrency,
      },
      workExperience: this.workExperience.experiences.map((experience) => ({
        position: experience.position,
        organization: experience.organization,
        industries: experience.industries.values.map((industry) => ({
          id: industry.id,
          name: industry.name,
        })),
        expCountry: experience.expCountry,
        expCity: experience.expCity,
        fromMonth: experience.fromMonth,
        fromYear: experience.fromYear,
        toMonth: experience.toMonth,
        toYear: experience.toYear,
        stillWorking: experience.stillWorking,
        achievements: experience.achievements,
      })),
      educationInfo: this.educationInfo.educations.map((education) => ({
        eduType: education.eduType,
        institution: education.institution,
        faculty: education.faculty,
        specialty: education.specialty,
        fromMonth: education.fromMonth,
        fromYear: education.fromYear,
        toMonth: education.toMonth,
        toYear: education.toYear,
        stillLearning: education.stillLearning,
      })),
      additionalEducationInfo: this.educationInfo.additionalEducations.map((education) => ({
        courseName: education.courseName,
        institution: education.institution,
        documentName: education.documentName,
        courseLink: education.courseLink,
        certificate: education.certificate,
        fromMonth: education.fromMonth,
        fromYear: education.fromYear,
        toMonth: education.toMonth,
        toYear: education.toYear,
        stillLearning: education.stillLearning,
      })),
      additionalInfo: {
        aboutYourself: this.additionalInfo.aboutYourself,
        skills: this.additionalInfo.skills.values.map((skill) => ({
          id: skill.id,
          name: skill.name,
        })),
        languages: this.additionalInfo.languages.map((language) => ({
          languageId: language.languageId,
          skillId: language.skillId,
        })),
        driverLicense: this.additionalInfo.driverLicense,
        driverCategories: this.additionalInfo.driverCategories.values.map((category) => ({
          id: category.id,
          name: category.name,
        })),
        visibility: this.additionalInfo.visibility,
        companyWhitelist: this.additionalInfo.companyWhitelist.selected.map((company) => ({
          id: company.id,
          name: company.name,
        })),
        companyBlacklist: this.additionalInfo.companyBlacklist.selected.map((company) => ({
          id: company.id,
          name: company.name,
        })),
      },
    };
  }

  async loadInitialValues(isMounted: () => boolean = () => true): Promise<void> {
    await this.loadInitialValuesState.run(
      () => HttpClient.get<CandidateEditProfileEmptyResponse>(
        '/candidates/editProfile'
      ),
      {
        onSuccess: (response) => {
          if (!isMounted() || response.status !== 200) {
            return;
          }

          this.desiredPosition.update(response.response);
          this.workExperience.update(response.response);
          this.educationInfo.update(response.response);
          this.additionalInfo.update(response.response);
        },
        onError: (error) => {
          console.error(error);
        },
      }
    );
  }

  goBack(): void {
    if (!this.canGoBack) {
      return;
    }

    const prevStep = this.stepOrder[this.currentStepIndex - 1];
    if (prevStep) {
      this.activeStep = prevStep;
    }
  }

  goNext(): void {
    if (!this.canGoNext) {
      return;
    }

    const nextStep = this.stepOrder[this.currentStepIndex + 1];
    if (nextStep) {
      this.activeStep = nextStep;
    }
  }

  goToStep(step: CreateResumeStep): void {
    if (this.isStepUnlocked(step)) {
      this.activeStep = step;
    }
  }

  setActiveStep(step: CreateResumeStep): void {
    this.activeStep = step;
  }

  isStepUnlocked(step: CreateResumeStep): boolean {
    const targetIndex = this.stepOrder.indexOf(step);
    if (targetIndex <= 0) {
      return true;
    }

    for (let index = 0; index < targetIndex; index += 1) {
      const prevStep = this.stepOrder[index];
      if (!this.isStepValid(prevStep)) {
        return false;
      }
    }

    return true;
  }

  private isStepValid(step: CreateResumeStep): boolean {
    switch (step) {
      case CreateResumeStep.DESIRED_POSITION:
        return this.desiredPosition.valid;
      case CreateResumeStep.WORK_EXPERIENCE:
        return this.workExperience.valid;
      case CreateResumeStep.EDUCATION_INFO:
        return this.educationInfo.valid;
      case CreateResumeStep.ADDITIONAL_INFO:
        return this.additionalInfo.valid;
      default:
        return false;
    }
  }

  dispose(): void {
    this.valuesLoggerDisposer();
    this.desiredPosition.dispose();
    this.workExperience.dispose();
    this.educationInfo.dispose();
    this.additionalInfo.dispose();
  }
}
